import { ChangeDetectorRef, Component, DestroyRef, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';

import type { Observable } from 'rxjs';
import { merge } from 'rxjs';

import type { FormGroupOf } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { FixedSelectOptionsHelper } from '@core/offers-logic';
import type { SalaryData } from '@core/offers-types';
import type { SelectOption } from '@core/user-interface-legacy';
import {
  FormFieldComponent,
  IconHelpComponent,
  LabelComponent,
  NumericInputComponent,
  ProtectedControlDirective,
  RtTooltip,
  SelectComponent,
} from '@core/user-interface-legacy';
import { CurrencySelectFieldComponent } from '@recruitee/currency-ui';

@Component({
  selector: 'rt-salary-form-group',
  templateUrl: './salary-form-group.component.html',
  styleUrls: ['./salary-form-group.component.less'],
  imports: [
    CurrencySelectFieldComponent,
    FormFieldComponent,
    FormsModule,
    IconHelpComponent,
    LabelComponent,
    ProtectedControlDirective,
    RtTooltip,
    SelectComponent,
    TranslatePipe,
    ReactiveFormsModule,
    NumericInputComponent,
  ],
})
export class SalaryFormGroupComponent {
  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly fixedSelectOptions = inject(FixedSelectOptionsHelper);

  private readonly destroyRef = inject(DestroyRef);

  public readonly salaryFormGroup = input<FormGroupOf<SalaryData>>(undefined!);
  public readonly isDisabled = input<boolean>(false);
  public readonly isReadOnly = input<boolean>(false);
  public readonly hasRequiredAsterisk = input<boolean>(false);
  public readonly updateFormattedValueDispatcher = input<Observable<void>>();
  public readonly showErrorMessages = input<boolean>(false);

  public salaryPeriodSelectOptions: SelectOption[] =
    this.fixedSelectOptions.getSalaryPeriodSelectOptions();

  public get showNotNumberError(): boolean {
    const salaryFormGroup = this.salaryFormGroup();
    return (
      salaryFormGroup.controls.min.errors?.notNumber ||
      salaryFormGroup.controls.max.errors?.notNumber
    );
  }

  public get showNotGraterThanOrEqualZeroError(): boolean {
    const salaryFormGroup = this.salaryFormGroup();
    return (
      salaryFormGroup.controls.min.errors?.notGreaterThanOrEqualToZero ||
      salaryFormGroup.controls.max.errors?.notGreaterThanOrEqualToZero
    );
  }

  public get showRangeError(): boolean {
    const salaryFormGroup = this.salaryFormGroup();
    return salaryFormGroup.controls.min.errors?.range || salaryFormGroup.controls.max.errors?.range;
  }

  public ngOnInit() {
    const salaryFormGroup = this.salaryFormGroup();
    if (salaryFormGroup) {
      const minControl = salaryFormGroup.controls.min;
      const maxControl = salaryFormGroup.controls.max;

      merge(minControl.valueChanges, maxControl.valueChanges)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          minControl.updateValueAndValidity({ emitEvent: false });
          maxControl.updateValueAndValidity({ emitEvent: false });
          this.cdRef.detectChanges();
        });
    }
  }
}
